import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import ProductCard from "@/components/ProductCard";
import WhatsAppButton from "@/components/WhatsAppButton";
import RecentlyViewed from "@/components/RecentlyViewed";
import Bestsellers from "@/components/Bestsellers";
import CategoryIcons from "@/components/MobileCategoryIcons";
import TrustBadgesStrip from "@/components/TrustBadgesStrip";
import BraceletGiftHome from "@/components/BraceletGiftHome";
import auraLogo from "@/assets/uma-aura-logo.svg";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { fetchProducts } from "@/services/productService";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useEffect, useState, useMemo } from "react";
import { ArrowUpRight, Facebook, Instagram, MessageCircle, Radio, Search, Youtube } from "lucide-react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Helmet } from "react-helmet";
import { HomeFaq } from "@/components/HomeFaq";
import { HOME_FAQS, getFaqAnswerText } from "@/content/homeFaqContent";
import { getCategoryPath, getProductCategory, PRODUCT_CATEGORIES } from "@/lib/productCategories";

const HOME_URL = "https://umasaura.com/";
const HOME_TITLE = "Crystals, Rudraksha & Spiritual Products | Uma's Aura";
const HOME_DESCRIPTION =
  "Explore authentic crystals, gemstone bracelets, rudraksha, malas, yantras and idols from Uma's Aura, with clear product guidance and delivery across India.";
const SOCIAL_IMAGE_URL = "https://umasaura.com/social-preview.png";

const Index = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialSearch = searchParams.get("search") || "";
  const urlCategory = searchParams.get("category");
  const [searchQuery, setSearchQuery] = useState(initialSearch);

  useEffect(() => {
    const legacyCategory = getProductCategory(urlCategory || undefined);
    if (!legacyCategory) return;
    const remainingParams = new URLSearchParams(searchParams);
    remainingParams.delete("category");
    const suffix = remainingParams.toString();
    navigate(`${getCategoryPath(legacyCategory.key)}${suffix ? `?${suffix}` : ""}`, { replace: true });
  }, [navigate, searchParams, urlCategory]);

  const { data: products, isLoading, error } = useQuery({
    queryKey: ["products"],
    queryFn: () => fetchProducts(""),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Group products by category
  const productsByCategory = useMemo(() => {
    if (!products) return {};
    
    let filtered = products;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = products.filter(p => 
        p.name.toLowerCase().includes(query) || 
        p.description?.toLowerCase().includes(query)
      );
    }

    const grouped: Record<string, typeof products> = {};
    PRODUCT_CATEGORIES.forEach(cat => {
      const categoryProducts = filtered.filter(p => p.category === cat.key);
      if (categoryProducts.length > 0) {
        grouped[cat.key] = categoryProducts;
      }
    });
    return grouped;
  }, [products, searchQuery]);

  // Every homepage category stays visible; legacy category URLs redirect above.
  const categoriesToDisplay = useMemo(() => {
    return PRODUCT_CATEGORIES.filter(cat => productsByCategory[cat.key]?.length > 0);
  }, [productsByCategory]);

  const totalProducts = useMemo(() => {
    return Object.values(productsByCategory).reduce((sum, arr) => sum + arr.length, 0);
  }, [productsByCategory]);

  const homeStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${HOME_URL}#organization`,
        name: "Uma's Aura",
        legalName: "VISWAVIRAT CORPORATE SERVICES PRIVATE LIMITED",
        url: HOME_URL,
        logo: "https://umasaura.com/icons/icon-512.png",
        email: "supportumasaura@gmail.com",
        telephone: "+91-9100203707",
        sameAs: [
          "https://www.youtube.com/@umas_aura",
          "https://www.facebook.com/profile.php?id=61581374596953",
          "https://www.instagram.com/umas_aura/",
          "https://whatsapp.com/channel/0029Vb7gD5I1NCrYCY7Foj1M",
        ],
        address: {
          "@type": "PostalAddress",
          streetAddress: "Flat no: 8-1-299/103&104/AA/SF-1, sy.no.75/a&76, Block A, Aparna Astute Commercial Complex",
          addressLocality: "Hyderabad",
          addressRegion: "Telangana",
          postalCode: "500008",
          addressCountry: "IN",
        },
      },
      {
        "@type": "WebSite",
        "@id": `${HOME_URL}#website`,
        url: HOME_URL,
        name: "Uma's Aura",
        publisher: { "@id": `${HOME_URL}#organization` },
        inLanguage: "en-IN",
      },
      {
        "@type": "FAQPage",
        "@id": `${HOME_URL}#faq`,
        mainEntity: HOME_FAQS.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: getFaqAnswerText(faq) },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang="en-IN" />
        <title>{HOME_TITLE}</title>
        <meta name="description" content={HOME_DESCRIPTION} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={HOME_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={HOME_URL} />
        <meta property="og:title" content={HOME_TITLE} />
        <meta property="og:description" content={HOME_DESCRIPTION} />
        <meta property="og:site_name" content="Uma's Aura" />
        <meta property="og:image" content={SOCIAL_IMAGE_URL} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Uma's Aura crystals, rudraksha and spiritual products" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={HOME_TITLE} />
        <meta name="twitter:description" content={HOME_DESCRIPTION} />
        <meta name="twitter:image" content={SOCIAL_IMAGE_URL} />
        <meta name="twitter:image:alt" content="Uma's Aura crystals, rudraksha and spiritual products" />
        <script type="application/ld+json">{JSON.stringify(homeStructuredData)}</script>
      </Helmet>
      <Navbar searchQuery={searchQuery} onSearchChange={setSearchQuery} />

      <main>
      {/* Hero Wrapper */}
      <div className="hero-wrapper relative w-full overflow-hidden m-0 p-0">
        <Hero />
      </div>
      
      {/* Category Icons */}
      <CategoryIcons />
      
      {/* Mobile: Trust Badges Scrolling Strip */}
      <TrustBadgesStrip />

      <section aria-labelledby="home-introduction" className="border-b border-amber-100 bg-gradient-to-b from-white to-[#fffaf0] px-4 py-10 md:py-14">
        <div className="container mx-auto max-w-5xl text-center">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.24em] text-amber-800">Thoughtfully selected in India</p>
          <h1 id="home-introduction" className="text-balance text-3xl font-bold tracking-tight text-foreground md:text-5xl">
            Authentic crystals and spiritual products for meaningful everyday rituals
          </h1>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-7 text-muted-foreground md:text-lg md:leading-8">
            Discover gemstone bracelets, rudraksha, malas, yantras, idols and astrology-informed guidance from Uma's Aura. Each listing offers clear product details so you can choose with confidence and care.
          </p>
        </div>
      </section>

      {!searchQuery.trim() && <BraceletGiftHome products={products || []} />}

      {/* Recently Viewed Section */}
      <div className="container mx-auto px-4">
        <RecentlyViewed />
      </div>

      {!searchQuery.trim() && <Bestsellers products={products || []} />}

      {/* Mobile Search Bar */}
      <section className="lg:hidden py-3 bg-white">
        <div className="container mx-auto px-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="search"
              className="w-full pl-10 pr-4 py-3 bg-muted/50 border focus-visible:ring-1"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for products..."
            />
          </div>
        </div>
      </section>

      {/* Search Results Info */}
      {searchQuery && (
        <div className="bg-white py-2">
          <div className="container mx-auto px-4 text-center">
            {searchQuery && (
              <p className="text-sm text-muted-foreground">
                Found {totalProducts} product{totalProducts !== 1 ? 's' : ''} for "{searchQuery}"
              </p>
            )}
          </div>
        </div>
      )}

      {/* Products by Category */}
      {isLoading ? (
        <div className="flex justify-center items-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-muted-foreground">
          Failed to load products. Please try again later.
        </div>
      ) : (
        categoriesToDisplay.map((category) => {
          const categoryProducts = productsByCategory[category.key];
          if (!categoryProducts || categoryProducts.length === 0) return null;
          const displayedProducts = searchQuery.trim() ? categoryProducts : categoryProducts.slice(0, 4);
          
          return (
            <section key={category.key} id={category.key} className="home-product-category py-8 bg-white border-b border-border scroll-mt-[130px] md:scroll-mt-[140px]">
              <div className="w-full px-2 md:container md:mx-auto md:px-4">
                <div className="text-center mb-6">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                    {category.label}
                  </h2>
                  <span className="text-sm text-muted-foreground">
                    {categoryProducts.length} item{categoryProducts.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
                  {displayedProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      {...product}
                    />
                  ))}
                </div>
                {!searchQuery.trim() && categoryProducts.length > displayedProducts.length && (
                  <div className="mt-6 text-center">
                    <Button asChild variant="outline">
                      <Link to={getCategoryPath(category.key)}>View all {categoryProducts.length} {category.label.toLowerCase()}</Link>
                    </Button>
                  </div>
                )}
              </div>
            </section>
          );
        })
      )}

      {/* Services Banner */}
      <section id="services" className="py-16 px-4 bg-gradient-to-r from-orange-600 to-yellow-600 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Consult Expert Astrologers
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get personalized readings and guidance from certified astrology experts
          </p>
          <Button 
            size="lg" 
            variant="secondary" 
            className="font-semibold"
            onClick={() => window.open('https://wa.me/919100203707?text=Hi%2C%20I%20would%20like%20to%20book%20a%20consultation', '_blank')}
          >
            Book Consultation Now
          </Button>
        </div>
      </section>
      <HomeFaq />
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-white py-12 px-4">
        <div className="container mx-auto">
          <div className="mb-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <img src={auraLogo} alt="Uma's Aura Logo" width={613} height={234} className="h-16 w-auto mb-4" />
              <p className="text-sm text-muted-foreground">
                Discover thoughtfully selected crystals, rudraksha, gemstone bracelets, malas, yantras and idols. Explore clear product details and get help with orders across India.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/guides/index.html" className="text-muted-foreground hover:text-foreground">Blog</Link></li>
                <li><Link to="/contact-us" className="text-muted-foreground hover:text-foreground">Contact Us</Link></li>
                <li><Link to="/terms-conditions" className="text-muted-foreground hover:text-foreground">Terms & Conditions</Link></li>
                <li><Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy Policy</Link></li>
                <li><Link to="/shipping-policy" className="text-muted-foreground hover:text-foreground">Shipping Policy</Link></li>
                <li><Link to="/cancellation-refunds" className="text-muted-foreground hover:text-foreground">Cancellation & Refunds</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Categories</h3>
              <ul className="space-y-2 text-sm">
                {PRODUCT_CATEGORIES.map((category) => (
                  <li key={category.key}>
                    <Link to={getCategoryPath(category.key)} className="text-muted-foreground hover:text-foreground">{category.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Contact Us</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Email: <a href="mailto:supportumasaura@gmail.com" className="break-all hover:text-foreground hover:underline">supportumasaura@gmail.com</a></li>
                <li>Phone: +91 9100203707</li>
                <li>GSTIN: 36AAKCV9929L1ZD</li>
              </ul>
              <nav className="mt-5 border-t border-border pt-4" aria-label="Uma's Aura social links">
                <h4 className="mb-2 text-sm font-semibold text-foreground">Follow &amp; connect</h4>
                <ul className="space-y-1 text-sm">
                  <li><a href="https://www.youtube.com/@umas_aura" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-9 items-center gap-2 text-muted-foreground hover:text-primary"><Youtube aria-hidden="true" className="h-4 w-4" />YouTube</a></li>
                  <li><a href="https://www.facebook.com/profile.php?id=61581374596953" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-9 items-center gap-2 text-muted-foreground hover:text-primary"><Facebook aria-hidden="true" className="h-4 w-4" />Facebook</a></li>
                  <li><a href="https://www.instagram.com/umas_aura/" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-9 items-center gap-2 text-muted-foreground hover:text-primary"><Instagram aria-hidden="true" className="h-4 w-4" />Instagram</a></li>
                  <li><a href="https://api.whatsapp.com/send/?phone=919100203707" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-9 items-center gap-2 text-muted-foreground hover:text-primary"><MessageCircle aria-hidden="true" className="h-4 w-4" />WhatsApp chat</a></li>
                </ul>
                <a href="https://whatsapp.com/channel/0029Vb7gD5I1NCrYCY7Foj1M" target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-semibold text-green-900 hover:border-green-400 hover:bg-green-100">
                  <Radio aria-hidden="true" className="h-4 w-4" />Follow WhatsApp Channel<ArrowUpRight aria-hidden="true" className="h-4 w-4" />
                </a>
              </nav>
            </div>
          </div>
          <div className="border-t border-border pt-8 text-center">
            <p className="text-muted-foreground text-sm mb-2">
              © 2026 Uma's Aura. All rights reserved.
            </p>
            <p className="text-xs text-muted-foreground">
              Because You Deserve The Finest - Premium Spiritual Products
            </p>
          </div>
        </div>
      </footer>

      <WhatsAppButton />
    </div>
  );
};

export default Index;
